"use client";

import { useQuery } from "@tanstack/react-query";
import { AdvancedMarker, Map, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslations } from "next-intl";
import { type ComponentProps, type ReactNode, type SVGAttributes } from "react";
import { Image as DatoImage } from "react-datocms/image";

import { cn } from "@trip/utils";

import { FlagIcon } from "~/components/icons/flag-icon";
import { MarkerIcon } from "~/components/icons/marker-icon";
import { LoadingSpinner } from "~/components/loading-spinner";
import { H3 } from "~/components/typography";

const MAP_ID = "3a8a8e51be95eef";

interface GeoPoint {
  latitude: number;
  longitude: number;
}

export interface ItineraryLocation {
  address?: string;
  description?: string | ReactNode;
  geoPoint: GeoPoint;
  id: string;
  image?: ComponentProps<typeof DatoImage>;
  label?: string;
}

function DashedLine({ ...props }: SVGAttributes<SVGSVGElement>) {
  return (
    <svg width="100%" height="100%" {...props}>
      <defs>
        <pattern
          id="dashed-pattern"
          width="2"
          height="8"
          patternUnits="userSpaceOnUse"
        >
          <rect x="0" y="0" width="2" height="4" fill="black" rx="1" ry="1" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="2" height="100%" fill="url(#dashed-pattern)" />
    </svg>
  );
}

function ItineraryMap({ itinerary }: { itinerary: ItineraryLocation[] }) {
  const coreLib = useMapsLibrary("core");
  const coreQuery = useQuery({
    queryKey: ["itinerary", Boolean(coreLib), ...itinerary.map(({ id }) => id)],
    queryFn: async () => {
      if (coreLib) {
        const bounds = new coreLib.LatLngBounds();
        itinerary.forEach((location) => {
          bounds.extend({
            lat: location.geoPoint.latitude,
            lng: location.geoPoint.longitude,
          });
        });
        return bounds;
      }
      return null;
    },
  });

  const bounds = coreQuery.data;

  return (
    <div className="relative aspect-square h-auto grow overflow-hidden rounded-lg border border-neutral-200 sm:aspect-auto sm:h-72 md:h-96">
      {bounds ? (
        <Map
          mapId={MAP_ID}
          defaultBounds={bounds.toJSON()}
          defaultCenter={bounds.getCenter().toJSON()}
          defaultZoom={10}
          fullscreenControl={false}
          streetViewControl={false}
        >
          {itinerary.map((location, index) => (
            <AdvancedMarker
              key={location.id}
              position={{
                lat: location.geoPoint.latitude,
                lng: location.geoPoint.longitude,
              }}
            >
              <div
                className={`flex size-8 items-center justify-center rounded-full text-lg font-medium ${
                  index === 0
                    ? "text-white [background-image:var(--sunset)]"
                    : index === itinerary.length - 1
                      ? "text-white [background-image:var(--midnight)]"
                      : "bg-neutral-200 text-neutral-900"
                }`}
              >
                {(index + 1).toString()}
              </div>
            </AdvancedMarker>
          ))}
        </Map>
      ) : (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}

export function Itinerary({ itinerary }: { itinerary: ItineraryLocation[] }) {
  const t = useTranslations("TourDetails");
  return (
    <div className="relative items-stretch gap-8 sm:flex sm:flex-row">
      <div className="mb-8 sm:mb-0 sm:w-1/3 sm:shrink-0">
        <H3 className="mb-5">{t("itinerary")}</H3>
        {itinerary.map(({ label, id, address, description, image }, index) => {
          const isStart = index === 0;
          const isLast = index === itinerary.length - 1;
          return (
            <div key={id} className="grid grid-cols-[max-content_1fr] gap-x-4">
              <div className="flex h-full shrink-0 flex-col items-center gap-0.5">
                <div
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-full text-lg font-medium",
                    isStart
                      ? "zoom-in-125 text-white [background-image:var(--sunset)]"
                      : isLast
                        ? "zoom-in-125 text-white [background-image:var(--midnight)]"
                        : "bg-neutral-200 text-neutral-900",
                  )}
                >
                  {isStart ? <MarkerIcon /> : isLast ? <FlagIcon /> : index + 1}
                </div>
                {!isLast ? (
                  <DashedLine className="h-[32px] min-h-[32px] w-0.5 grow text-neutral-500" />
                ) : null}
              </div>
              <div className="pb-2">
                <div className="mt-1 grow font-medium">{label}</div>
                {address ? (
                  <div className="mt-1 grow text-sm font-medium text-neutral-500">
                    {address}
                  </div>
                ) : null}
                {description ? (
                  <div className="mt-1 grow text-sm text-neutral-500">
                    {typeof description === "string" ? (
                      // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
                      <div dangerouslySetInnerHTML={{ __html: description }} />
                    ) : (
                      description
                    )}
                  </div>
                ) : null}
                {image ? (
                  <DatoImage className="my-2 rounded-lg" data={image.data} />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="w-full grow">
        <div className="sticky top-24">
          <ItineraryMap itinerary={itinerary} />
        </div>
      </div>
    </div>
  );
}
